import { Schema } from 'effect'

/**
 * Patient search form schema (privacy-safe demographic search)
 */
export const PatientSearchSchema = Schema.Struct({
  lastname: Schema.String.pipe(Schema.check(Schema.isMinLength(1, { message: 'validation.required' }))),
  dob: Schema.String.pipe(Schema.check(Schema.isMinLength(1, { message: 'validation.required' }))),
  firstname: Schema.String
})

export type PatientSearchData = Schema.Schema.Type<typeof PatientSearchSchema>

/**
 * Search result status from the server
 */
export type PatientSearchStatus = 'FOUND' | 'AMBIGUOUS' | 'NOT_FOUND'

/**
 * Insurance type from search result
 */
export type SearchInsuranceType = 'GKV' | 'PKV' | 'SELFPAY' | 'UNKNOWN'

/**
 * Gender type
 */
const GenderSchema = Schema.Literals(['M', 'W', 'D', 'U'])
export type Gender = Schema.Schema.Type<typeof GenderSchema>

/**
 * Insurance type
 */
const InsuranceSchema = Schema.Literals(['GKV', 'PKV', 'SELFPAY'])
export type Insurance = Schema.Schema.Type<typeof InsuranceSchema>

/**
 * Gender as captured by the registration form: `""` until the patient picks
 * one. Nothing is pre-selected, so "Unbekannt" can no longer be submitted by
 * accident (KC-78).
 */
const GenderSelectionSchema = Schema.Literals(['M', 'W', 'D', 'U', ''])

/**
 * Patient registration basics form schema
 */
export const PatientBasicsSchema = Schema.Struct({
  familyName: Schema.String.pipe(Schema.check(Schema.isMinLength(1, { message: 'validation.required' }))),
  givenName: Schema.String.pipe(Schema.check(Schema.isMinLength(1, { message: 'validation.required' }))),
  dob: Schema.String.pipe(Schema.check(Schema.isMinLength(1, { message: 'validation.required' }))),
  gender: GenderSelectionSchema,
  insurance: InsuranceSchema,
  insurerName: Schema.optional(Schema.String)
})

export type PatientBasicsData = Schema.Schema.Type<typeof PatientBasicsSchema>

/**
 * Field-level validator for PKV insurer name (required when PKV is selected).
 * Co-located with the schema but applied at the field level in TanStack Form
 * to associate the error with the correct field for display.
 */
export function validateInsurerNameRequired(value: string | undefined): string | undefined {
  return !value || value.trim().length === 0 ? 'validation.required' : undefined
}

/**
 * Field-level validator for the gender choice: one option must be picked.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * validateGenderSelected('') // 'self-service.check-in.register-basics.gender-required'
 * ```
 */
export function validateGenderSelected(value: Gender | ''): string | undefined {
  return value === '' ? 'self-service.check-in.register-basics.gender-required' : undefined
}

/**
 * Patient registration details form schema (confirmation step with optional PKV fields)
 */
export const PatientDetailsSchema = Schema.Struct({
  policyNumber: Schema.optional(Schema.String)
})

export type PatientDetailsData = Schema.Schema.Type<typeof PatientDetailsSchema>

/**
 * Insurer search form schema
 */
const InsurerSearchSchema = Schema.Struct({
  searchQuery: Schema.String
})

export type InsurerSearchData = Schema.Schema.Type<typeof InsurerSearchSchema>
